// ════════════════════════════════════════════════════════════════════════
//  Query-Konfiguration
// ════════════════════════════════════════════════════════════════════════

import { QueryClient } from "@tanstack/react-query";

/**
 * Bewusst zurückhaltend eingestellt. Das Dashboard zeigt Klassenlisten und
 * Ergebnisse, die sich pro Woche ändern – nicht Börsenkurse. Aggressives
 * Nachladen bei jedem Fensterwechsel wäre nur Last ohne Nutzen.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        refetchOnWindowFocus: false,
        retry: (attempt, error) => {
          // 4xx sind Sachfehler (Recht, Tippfehler) – wiederholen hilft nicht.
          const status = (error as { status?: number } | null)?.status;
          if (typeof status === "number" && status >= 400 && status < 500) return false;
          return attempt < 2;
        },
      },
      mutations: { retry: 0 },
    },
  });
}

/**
 * Schlüssel an einer Stelle, damit Invalidierung nicht zum Ratespiel wird.
 *
 * WICHTIG: Zwei Abfragen mit unterschiedlicher Ergebnisform dürfen sich keinen
 * Schlüssel teilen. Genau das war einmal der Fall – eine Abfrage lieferte eine
 * Liste, die andere ein einzelnes Objekt, und die zweite Komponente rief
 * `.filter` auf einem Objekt auf. Der Fehler zeigte sich erst im Browser, weil
 * keine der beiden Komponenten allein falsch war.
 *
 * `neuesteFassung` liegt bewusst UNTER `fassungen`: so trifft eine Invalidierung
 * von `fassungen` beide, ohne dass sie sich den Cache-Eintrag teilen.
 */
export const queryKeys = {
  identity: ["identity"] as const,
  classes: (schoolId: string) => ["classes", schoolId] as const,
  schoolClass: (classId: string) => ["class", classId] as const,
  codes: (classId: string) => ["codes", classId] as const,
  results: (classId: string) => ["results", classId] as const,
  schule: (schoolId: string) => ["school", schoolId] as const,
  teachers: (schoolId: string) => ["teachers", schoolId] as const,
  /** Welche Lehrgänge eine Klasse führt. */
  classCourses: (classId: string) => ["class-courses", classId] as const,
  /**
   * Ob zu EINEM Lehrgang dieser Klasse schon etwas erreicht wurde. Je Lehrgang
   * getrennt: die Antwort entscheidet, ob seine Fassung noch nachziehen darf,
   * und das ist für jeden Lehrgang eine eigene Frage.
   */
  courseResults: (classId: string, curriculumId: string) =>
    ["class-course-results", classId, curriculumId] as const,
  /** Alle Lehrgänge mit ihrer neuesten veröffentlichten Fassung. */
  courseCatalog: ["course-catalog"] as const,
  /**
   * Wie weit die Führung durch die ersten Schritte gekommen ist. Je Person, nicht
   * je Schule: die Zeile gehört dem Menschen, und niemand sonst sieht sie.
   *
   * Und je APP: dieselbe Person kann in der Verwaltung und in der Admin-App
   * unterwegs sein. Ohne die App im Schlüssel teilten sich zwei Führungen einen
   * Cache-Eintrag – derselbe Fehler wie eine geteilte Tabellenzeile, nur im
   * Browser und damit erst beim zweiten Hinsehen zu erkennen.
   */
  onboarding: (userId: string, app: string) => ["onboarding", userId, app] as const,
};
